<template>
  <RfSimpleCard>
    <div class="settings-actions-tabs users-setting-tabs">
      <div :class="{ selectedTab: userTab === 'tab5' }">
        <button @click="userTab = 'tab5'">Pulse Users</button>
      </div>
      <div :class="{ selectedTab: userTab === 'tab4' }">
        <button @click="userTab = 'tab4'">Organizations</button>
      </div>
      <div :class="{ selectedTab: userTab === 'tab2' }">
        <button @click="userTab = 'tab2'">Test Users</button>
      </div>
      <div :class="{ selectedTab: userTab === 'tab3' }">
        <button @click="userTab = 'tab3'">User Lookup</button>
      </div>
      <div :class="{ selectedTab: userTab === 'tab1' }">
        <button @click="userTab = 'tab1'">Global Limits</button>
      </div>
    </div>
    <div class="user-setting-content">
      <div v-if="userTab === 'tab1'">
        <div class="global-holdout-setting">
          <h4>Set Global Holdout percentage</h4>
          <div class="hold-cap-div">
            Lowering the holdout percentage will cause existing holdout users to become eligible for
            prompts
          </div>
          <VueSlider
            v-model="pathFilter.global_holdout_percentage"
            style="margin: 40px 10px 50px 10px"
            :height="5"
            :max="100"
            :marks="globalHoldoutPercentages"
            :lazy="true"
            :disabled="userStore.isReadOnly"
          />
        </div>
        <div class="global-frequency-cap-opt">
          <RfFrequencyCap v-if="currApp?.path_filter" :currApp="currApp" />
          <RfImpressionLimits v-if="pathFilter.impressions" v-model="pathFilter.impressions" />
        </div>
        <div v-if="currApp" class="global-minimum-delay">
          <h4>Overlay Interval</h4>
          <div class="hold-cap-div">
            Specify a minimum interval between overlay prompt impressions on web (page triggers
            only). The interval resets if the browser tab is closed.
          </div>
          <div class="inline-flex items-center gap-3">
            <v-text-field
              variant="outlined"
              density="compact"
              persistentPlaceholder
              type="number"
              :modelValue="globalInterval[0]"
              hideDetails
              :disabled="userStore.isReadOnly"
              bgColor="#ffffff"
              class="show-interval-opt-num"
              @update:modelValue="v => (globalInterval = [v, globalInterval[1]])"
            />
            <v-select
              :items="timeUnits"
              :modelValue="globalInterval[1]"
              persistentPlaceholder
              variant="outlined"
              required
              density="compact"
              hideDetails
              :disabled="userStore.isReadOnly"
              bgColor="#ffffff"
              class="show-interval-opt-timeunits"
              @update:modelValue="v => (globalInterval = [globalInterval[0], v])"
            />
          </div>
        </div>
      </div>

      <div v-if="userTab === 'tab2'">
        <div class="add-testusers-div">
          <div class="add-testusers-label">Designate test user:</div>
          <div class="mt-7 flex grow-1">
            <div class="add-testusers-id">
              <RfTextInput
                v-model="newTestUserId"
                hideDetails
                variant="outlined"
                density="compact"
                placeholder="e.g. 123456789"
                title="User Id"
              />
              <RfTextInput
                v-model="newTestUserDesc"
                hideDetails
                variant="outlined"
                density="compact"
                placeholder="Description"
                title="Description"
              />
            </div>
            <RfButton
              icon="md:add"
              buttonText="Add"
              type="submit"
              color="accent"
              class="mt-5"
              :disabled="
                userStore.isDisabledWithMessage || (!newTestUserId && 'Missing test user ID')
              "
              @click="createTestUser"
            />
          </div>
        </div>
        <v-table class="rf-settings-table-data">
          <template #default>
            <thead>
              <tr>
                <th style="min-width: 200px" class="text-left">User Id</th>
                <th style="min-width: 200px" class="text-left">Description</th>
                <th class="text-left">Reset Clicks</th>
                <th class="text-left">Remove Test User Designation</th>
              </tr>
            </thead>
            <tbody v-if="testUsers?.length">
              <tr v-for="(item, index) in testUsers" :key="index">
                <td>{{ item.user_uid }}</td>
                <td>{{ item.metadata.description }}</td>
                <td>
                  <RfButton
                    icon="md:autorenew"
                    color="#AFBABF"
                    variant="text"
                    :disabled="userStore.isDisabledWithMessage"
                    @click="onResetGoals(item)"
                  />
                </td>
                <td>
                  <RfButton
                    icon="md:clear"
                    color="error"
                    variant="text"
                    :disabled="userStore.isDisabledWithMessage"
                    @click="onDeleteTestUser(item)"
                  />
                </td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr>
                <td colspan="3">
                  <div class="settings-no-data-message">Nothing to show.</div>
                </td>
              </tr>
            </tbody>
          </template>
        </v-table>
      </div>
      <div v-if="userTab === 'tab3'">
        <div class="fup-text">Look up a user's traits and prompts.</div>
        <div class="fetch-user-promo-ct [&>:not(button)]:flex-1">
          <RfTextInput
            v-model="getActionsUserId"
            hideDetails
            variant="outlined"
            density="compact"
            placeholder="e.g. 123456789"
            title="User Id"
            bgColor="#fff"
          />
          <div class="flex shrink flex-col gap-1">
            <label class="rf-text-input--title text-body text-grey-1">Device Type</label>
            <v-select
              v-model="deviceType"
              variant="outlined"
              density="compact"
              persistentPlaceholder
              hideDetails
              :items="devices"
              itemTitle="text"
              placeholder="Select configuration"
              height="32px"
              bgColor="#ffffff"
              style="min-width: 160px"
              :menuProps="{ maxHeight: 410, offsetY: true }"
            />
          </div>
          <v-btn
            variant="flat"
            type="submit"
            color="accent"
            class="mt-5"
            :disabled="!getActionsUserId"
            @click="getActionsForEndUser"
          >
            View
          </v-btn>
        </div>
      </div>
      <div v-if="userTab === 'tab4'">
        <div class="add-user-header-div">
          <div class="add-user-title">
            <h4>Organizations</h4>
          </div>
          <RfButton
            icon="md:add"
            buttonText="Add organization"
            color="success"
            class="px-7"
            :disabled="userStore.isDisabledExtendedWithMessage"
            @click="openAddOrgModal"
          />
        </div>
        <v-table class="rf-settings-table-data">
          <template #default>
            <thead>
              <tr>
                <th class="text-left">Name</th>
                <th v-if="userStore.isCurrentSuperAdmin" class="text-left">Key</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in orgs" :key="index">
                <td>{{ item.name }}</td>
                <td v-if="userStore.isCurrentSuperAdmin">
                  {{ item.key }}
                </td>
                <td>
                  <div class="settings-button-wrapper">
                    <template v-if="userStore.isAdmin && item.key !== 'default'">
                      <RfButton
                        icon="md:edit"
                        color="accent"
                        variant="text"
                        :disabled="userStore.isDisabledExtendedWithMessage"
                        @click="onEditOrg(item)"
                      />
                      <RfButton
                        icon="md:clear"
                        color="error"
                        variant="text"
                        :disabled="userStore.isDisabledExtendedWithMessage"
                        @click="showConfirmDeleteOrgDialog(item.id)"
                      />
                    </template>
                  </div>
                </td>
              </tr>
            </tbody>
          </template>
        </v-table>
      </div>
      <div v-if="userTab === 'tab5'">
        <div class="add-user-header-div">
          <div class="add-user-title">
            <h4>Users Manager</h4>
          </div>
          <RfButton
            icon="md:add"
            buttonText="Add user"
            color="success"
            class="px-7"
            :disabled="userStore.isDisabledExtendedWithMessage"
            @click="openAddUserModal"
          />
        </div>
        <v-table class="rf-settings-table-data">
          <template #default>
            <thead>
              <tr>
                <th class="text-left">Name</th>
                <th class="text-left">Email</th>
                <th class="text-left">Status</th>
                <th class="text-left">Role</th>
                <th
                  v-if="currApp?.company_sso_enabled || currentCompany?.sso_properties"
                  class="text-left"
                >
                  IDP
                </th>
                <th style="width: 100px"></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in usersStore.currUsers" :key="item.id">
                <td>{{ item.first_name }} {{ item.last_name }}</td>
                <td>{{ item.email }}</td>
                <td>{{ item.invitation_accepted ? "Active" : "Invited" }}</td>
                <td>
                  <div>{{ displayRole(item) }}</div>
                  <div v-if="isAdminingCompany">
                    Allowed apps: {{ displayAllowedAppsText(item) }}
                  </div>
                </td>
                <td v-if="currApp?.company_sso_enabled || currentCompany?.sso_properties">
                  {{ item.sso_idp }}
                </td>
                <td>
                  <div class="settings-button-wrapper">
                    <template v-if="userStore.isAdmin">
                      <RfButton
                        icon="md:edit"
                        color="accent"
                        variant="text"
                        :disabled="!userStore.canModifyUser(item.role) && 'Insufficient Role'"
                        @click="onEditUser(item)"
                      />
                      <RfButton
                        icon="md:clear"
                        color="error"
                        variant="text"
                        :disabled="!userStore.canModifyUser(item.role) && 'Insufficient Role'"
                        @click="showConfirmDeleteDialog(item.id)"
                      />
                    </template>
                  </div>
                </td>
              </tr>
            </tbody>
          </template>
        </v-table>
      </div>
    </div>
    <v-dialog v-model="showDeleteOrgDialog" persistent maxWidth="600" width="550">
      <v-card>
        <v-card-title class="headline">Are you sure?</v-card-title>
        <v-card-text>
          Deleting an organization cannot be undone. Existing prompts, segments, users, and all
          other items will be re-assigned to the selected organization.
          <RfSelect
            v-model="reassignedOrgId"
            density="compact"
            variant="outlined"
            title="Organizations"
            name="orgs"
            class="mt-5"
            hideDetails
            itemTitle="text"
            :items="assignableOrgs"
          />
        </v-card-text>

        <v-card-actions class="modal-card-actions-div">
          <v-btn class="cancel-btn px-7!" variant="outlined" @click="closeDeleteOrgDialog()"
            >Cancel</v-btn
          >
          <v-btn
            color="error"
            variant="flat"
            class="px-10!"
            :disabled="!reassignedOrgId"
            @click="onDeleteOrg()"
            >Delete</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showAddEditOrgDialog" persistent maxWidth="600" width="550">
      <v-card tag="form" @submit.prevent="onSubmitOrg">
        <v-card-title class="headline">{{
          org.id ? "Edit Organization" : "Add Organization"
        }}</v-card-title>
        <v-card-text>
          <div class="add-users-form-div">
            <RfTextInput
              v-model="org.name"
              placeholder="Marketing"
              variant="outlined"
              density="compact"
              title="Name"
              persistentHint
              hint="Specify an organization name role."
            ></RfTextInput>
          </div>
        </v-card-text>
        <v-card-actions class="modal-card-actions-div">
          <v-btn class="cancel-btn px-7!" variant="outlined" @click="showAddEditOrgDialog = false">
            Cancel
          </v-btn>
          <v-btn v-if="org.id" variant="flat" type="submit" color="success" class="px-10!">
            <v-icon start>md:update</v-icon>Update Organization
          </v-btn>
          <v-btn v-else variant="flat" type="submit" color="success" class="px-10!">
            <v-icon start>md:add</v-icon>Add Org
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showDeleteUserDialog" persistent maxWidth="600" width="550">
      <v-card>
        <v-card-title class="headline">Are you sure?</v-card-title>
        <v-card-text>Deleting a user cannot be undone.</v-card-text>
        <v-card-actions class="modal-card-actions-div">
          <v-btn class="cancel-btn px-7!" variant="outlined" @click="closeDeleteDialog()"
            >Cancel</v-btn
          >
          <v-btn color="error" variant="flat" class="px-10!" @click="onDeleteUser()">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showAddEditUserDialog" persistent maxWidth="600" width="550">
      <v-card tag="form" @submit.prevent="onSubmitUser">
        <v-card-title class="headline">{{ user.id ? "Edit User" : "Add User" }}</v-card-title>

        <v-card-text>
          <div class="mb-2 flex flex-col gap-2">
            <span>
              Specify user's name, email, and role. Refer to the
              <a target="_blank" href="https://docs.recurly.com/recurly-engage/docs/pulse-users">
                documentation
              </a>
              for additional information.
            </span>

            <RfAlert v-if="isAdminingCompany" type="error">
              Only create Super Admins using this view since organizations cannot be set. All of the
              customer's users should be created in the settings section after logging into the
              correct app
            </RfAlert>
          </div>
          <v-form
            ref="userForm"
            v-model="isValid"
            :validateOn="user.id ? 'eager' : 'input lazy'"
            class="add-users-form-div"
          >
            <RfTextInput
              v-model="user.first_name"
              placeholder="John"
              variant="outlined"
              density="compact"
              title="First Name"
              name="first-name"
              autocomplete="given-name"
              :rules="[v => !!v || 'First Name field is required.']"
            />
            <RfTextInput
              v-model="user.last_name"
              placeholder="Doe"
              variant="outlined"
              density="compact"
              title="Last Name"
              name="last-name"
              autocomplete="family-name"
              :rules="[v => !!v || 'Last Name field is required.']"
            />
            <RfTextInput
              v-model="user.email"
              variant="outlined"
              density="compact"
              type="email"
              title="Email"
              name="email"
              autocomplete="email"
              :rules="[
                v => !!v || 'Email field is required.',
                v =>
                  !!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(v) ||
                  'Must be a valid e-mail.',
              ]"
              placeholder="name@emailaddress.com"
            />

            <RfSelect
              v-if="userStore.isCurrentSuperAdmin"
              v-model="user.departments"
              density="compact"
              variant="outlined"
              title="Departments"
              multiple
              name="departments"
              :items="selectableDepartments"
              itemTitle="text"
            />
            <div class="app-user-role-row">
              <template v-if="userStore.isCurrentSuperAdmin || isAdminingCompany">
                <RfSelect
                  v-if="appUsers[currApp?.id]"
                  v-model="appUsers[currApp.id].org_id"
                  density="compact"
                  variant="outlined"
                  title="Organizations"
                  name="orgs"
                  :items="orgsSelection"
                  itemTitle="text"
                ></RfSelect>
                <div class="company-user-role">
                  <label>Company Role</label>
                  <v-radio-group v-model="user.role" hideDetails inline>
                    <v-radio label="Pending" value="pending"></v-radio>
                    <v-radio label="Member" value="member"></v-radio>
                    <v-radio label="Admin" value="admin"></v-radio>
                  </v-radio-group>
                </div>
                <div v-if="user.role === 'member'">
                  <div v-for="{ name, id } in apps" :key="id" class="company-user-role">
                    <label>{{ name }} App Role</label>
                    <v-radio-group
                      v-if="appUsers[id]"
                      v-model="appUsers[id].role"
                      hideDetails
                      inline
                    >
                      <v-radio label="None" value=""></v-radio>
                      <v-radio
                        v-for="(value, key) in APP_ROLES"
                        :key="key"
                        :label="value"
                        :value="key"
                      ></v-radio>
                    </v-radio-group>
                  </div>
                </div>
              </template>
              <div v-else-if="user.role === 'member' && appUsers[currApp?.id]">
                <v-radio-group
                  v-model="appUsers[currApp.id].role"
                  inline
                  hideDetails
                  class="thirdparty-radio"
                >
                  <v-radio
                    v-for="(value, key) in APP_ROLES"
                    :key="key"
                    :label="value"
                    :value="key"
                  ></v-radio>
                </v-radio-group>
                <RfSelect
                  v-model="appUsers[currApp.id].org_id"
                  density="compact"
                  variant="outlined"
                  title="Organizations"
                  name="orgs"
                  :items="orgsSelection"
                  itemTitle="text"
                ></RfSelect>
              </div>
              <v-checkbox
                v-model="isCorrect"
                label="Are you sure this information is correct?"
                hideDetails
              />
            </div>
          </v-form>
        </v-card-text>
        <v-card-actions class="modal-card-actions-div">
          <v-btn class="cancel-btn px-7!" variant="outlined" @click="showAddEditUserDialog = false">
            Cancel
          </v-btn>
          <v-btn
            variant="flat"
            :disabled="!isCorrect || !isValid"
            class="px-7!"
            type="submit"
            color="success"
          >
            <template v-if="user.id"> <v-icon start>md:update</v-icon>Update User</template>
            <template v-else> <v-icon start>md:add</v-icon>Add User </template>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="userDataSelectionDialog" persistent maxWidth="600" width="560">
      <v-card>
        <v-card-title class="headline">Select Data Type</v-card-title>
        <v-card-text>
          Choose which data you'd like to view for user {{ getActionsUserId }}:
        </v-card-text>
        <v-card-actions class="modal-card-actions-div">
          <v-btn
            class="cancel-btn mr-4 px-5"
            variant="outlined"
            @click="userDataSelectionDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn variant="flat" color="accent" class="px-4" @click="showPingData">
            Ping Payload
          </v-btn>
          <v-btn
            v-if="userStore.isCurrentSuperAdmin"
            variant="flat"
            color="accent"
            class="px-4"
            @click="showDebugPingData"
          >
            Debug Ping
          </v-btn>
          <v-btn variant="flat" color="success" class="px-4" @click="showTraitsData">
            Custom Traits
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="actionsUserDataDialog" scrollable maxWidth="900">
      <v-card>
        <v-card-title class="headline">
          {{
            selectedDataType === "traits"
              ? "Custom Traits"
              : selectedDataType === "debug"
                ? "Debug Ping"
                : "User Data"
          }}
          for {{ getActionsUserId }}
        </v-card-title>
        <v-card-text class="promotion-priority-div">
          <div v-if="selectedDataType === 'traits'" class="traits-display">
            <div v-if="traitsArray.length > 0">
              <v-text-field
                v-model="traitsSearch"
                label="Search by trait name or value"
                prependInnerIcon="mdi:mdi-magnify"
                variant="outlined"
                density="compact"
                hideDetails
                clearable
                class="mb-4"
              />
              <v-data-table
                :headers="traitsTableHeaders"
                :items="traitsArray"
                :itemsPerPage="25"
                :sortBy="[{ key: 'updated_at', order: 'desc' }]"
                :search="traitsSearch"
                class="rf-settings-table-data"
              />
            </div>
            <div v-else class="pa-4">No traits found for this user.</div>
          </div>
          <div v-else-if="selectedDataType === 'debug'">
            <v-progress-linear v-if="!debugData" indeterminate />
            <v-table v-else>
              <thead>
                <tr>
                  <th class="w-px text-center whitespace-nowrap">Matched</th>
                  <th>Segment</th>
                  <th>Filters</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(data, slug) in debugData" :key="slug">
                  <td class="text-center text-xl">
                    {{ data.matched ? "✅" : "❌" }}
                  </td>
                  <td class="py-3">
                    <router-link
                      :to="`/apps/${currApp.id}/segments/${slug}`"
                      class="font-weight-medium"
                    >
                      {{ data.name }}
                    </router-link>
                  </td>
                  <td class="py-3">
                    <div class="flex flex-wrap gap-1">
                      <v-chip
                        v-for="(result, key) in segmentFilters(data)"
                        :key="key"
                        size="small"
                        :color="result === 'MATCH' ? 'success' : 'error'"
                        >{{ key }}</v-chip
                      >
                    </div>
                  </td>
                </tr>
              </tbody>
            </v-table>
          </div>
          <div v-else class="get-actions-user-data">
            <pre v-if="getActionsUserData">{{ getActionsUserData }}</pre>
          </div>
        </v-card-text>
        <v-card-actions class="modal-card-actions-div">
          <v-btn
            class="cancel-btn"
            variant="outlined"
            style="width: 100px"
            @click.stop="actionsUserDataDialog = false"
            >Close</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </RfSimpleCard>
</template>

<script>
import { ref } from "vue";
import VueSlider from "vue-slider-component";
import "vue-slider-component/theme/default.css";
import RfSimpleCard from "@/components/RfSimpleCard.vue";
import RfTextInput from "@/components/legacy/RfInputLegacy.vue";
import RfSelect from "@/components/legacy/RfSelectLegacy.vue";
import RfFrequencyCap from "@/views/retentions/RfFrequencyCap.vue";
import RfImpressionLimits from "@/views/retentions/RfImpressionLimits.vue";
import RfButton from "@/components/buttons/RfButton.vue";
import ApiApps from "@/apis/ApiApps";
import ApiDebug from "@/apis/ApiDebug";
import { useToastsStore } from "@/pinia/toastsStore";
import { useLegacyLastErrorStore } from "@/pinia/legacyLastErrorStore";
import ConduitApiUsers from "@/apis/ConduitApiUsers";
import { useUsersStore } from "@/pinia/usersStore";
import { useAppsStore } from "@/pinia/appsStore";
import { useEndUserStore } from "@/pinia/endUserStore";
import { ADMIN, APP_ROLES, SUPER_ADMIN, useUserStore } from "@/pinia/userStore";
import { useOrgsStore } from "@/pinia/orgsStore";
import { getAllDevices } from "@/utils/getDeviceName";
import RfAlert from "@/components/alerts/RfAlert.vue";

export default {
  name: "RfManageUsers",
  components: {
    VueSlider,
    RfFrequencyCap,
    RfImpressionLimits,
    RfSimpleCard,
    RfTextInput,
    RfSelect,
    RfButton,
    RfAlert,
  },
  props: { currentCompany: Object, currApp: Object, tab: { type: String, default: undefined } },
  setup: () => ({
    toastsStore: useToastsStore(),
    usersStore: useUsersStore(),
    appsStore: useAppsStore(),
    endUserStore: useEndUserStore(),
    userStore: useUserStore(),
    orgsStore: useOrgsStore(),
    APP_ROLES,
    isValid: ref(false),
  }),
  data() {
    return {
      user: {
        id: "",
        email: "",
        role: "member",
        departments: [],
        first_name: "",
        last_name: "",
      },
      org: { id: "", name: "", key: "" },
      appUsers: {},
      currentApps: null,
      newTestUserId: null,
      newTestUserDesc: null,
      showAddEditUserDialog: false,
      showDeleteUserDialog: false,
      userToDelete: null,
      showAddEditOrgDialog: false,
      showDeleteOrgDialog: false,
      orgToDelete: null,
      getActionsUserId: null,
      actionsUserDataDialog: false,
      userDataSelectionDialog: false,
      selectedDataType: null, // 'ping', 'traits', or 'debug'
      debugData: null,
      traitsSearch: "",
      globalHoldoutPercentages: new Array(21).fill().map((_, index) => index * 5),
      selectableDepartments: [
        { value: "sales", text: "Sales" },
        { value: "marketing", text: "Marketing" },
        { value: "support", text: "Support" },
      ],
      reassignedOrgId: null,
      innerTab: "tab5",
      isCorrect: false,
      timeUnits: ["seconds", "minutes", "hours"],
      deviceType: null,
    };
  },
  computed: {
    userTab: {
      get() {
        return this.tab || this.innerTab;
      },
      set(v) {
        this.innerTab = v;
        this.$emit("update:tab", v);
      },
    },
    orgs() {
      return this.orgsStore.orgs;
    },
    testUsers() {
      return this.endUserStore.results;
    },
    getActionsUserData() {
      return this.endUserStore.actions;
    },
    getUserTraitsData() {
      return this.endUserStore.traits;
    },
    traitsArray() {
      if (!this.getUserTraitsData?.traits) return [];

      return Object.entries(this.getUserTraitsData.traits).map(([label, data]) => ({
        label,
        value: data.value,
        updated_at: data.updated_at,
      }));
    },
    traitsTableHeaders() {
      return [
        { title: "Trait Name", key: "label", sortable: true },
        { title: "Value", key: "value", sortable: true },
        {
          title: "Last Updated",
          key: "updated_at",
          value: item => this.formatTimestamp(item.updated_at),
          sortable: true,
          searchable: false,
        },
      ];
    },
    apps() {
      return this.currentApps || this.appsStore.apps;
    },
    assignableOrgs() {
      return this.orgs
        .filter(item => item.id !== this.orgToDelete)
        .map(item => {
          return { value: item.id, text: item.name };
        });
    },
    isAdminingCompany() {
      return !this.currApp;
    },
    pathFilter() {
      return this.currApp?.path_filter || {};
    },
    orgsSelection() {
      return this.orgs.map(item => ({ value: item.id, text: item.name }));
    },
    globalInterval: {
      get() {
        const seconds = this.currApp?.path_filter?.url_trigger_overlay_interval_seconds || 0;
        if (seconds > 0) {
          if (seconds % 3600 === 0) return [seconds / 3600, "hours"];
          if (seconds % 60 === 0) return [seconds / 60, "minutes"];
        }
        return [seconds, "seconds"];
      },
      set(value) {
        let time = parseInt(value[0]) || 0;

        switch (value[1] || "seconds") {
          case "hours":
            time *= 3600;
            break;
          case "minutes":
            time *= 60;
            break;
          case "seconds":
          default:
            break;
        }

        this.currApp.path_filter.url_trigger_overlay_interval_seconds = time;
      },
    },
    devices() {
      return getAllDevices(this.currApp?.custom_devices, this.currApp?.flags?.custom_devices);
    },
  },
  watch: {
    "currentCompany.id": {
      handler() {
        this.usersStore.getUsers(this.currentCompany?.id, true);
        ApiApps.getApps(this.currentCompany?.id).then(apps => {
          this.currentApps = apps;
          this.appUsers = {};
          (apps || []).forEach(app => (this.appUsers[app.id] = { role: "" }));
        });
      },
      immediate: true,
    },
    "currApp": function (to) {
      if (to) {
        this.orgsStore.getOrgs(to.id);
      }
    },
  },
  mounted() {
    this.getTestUsers();
    this.setDefaultDeviceType();
  },
  methods: {
    displayRole(user) {
      if (user.role === ADMIN) return "company_admin";
      if (this.isAdminingCompany || user.role === SUPER_ADMIN) return user.role;
      return `app_${user.apps.find(app => app.id === this.currApp.id).role}`;
    },
    openAddOrgModal() {
      this.showAddEditOrgDialog = true;
      this.resetOrgForm();
    },
    resetOrgForm() {
      this.org.id = "";
      this.org.name = "";
      this.org.key = "";
    },
    openAddUserModal() {
      this.showAddEditUserDialog = true;
      this.resetUserForm();
    },
    resetUserForm() {
      this.user.id = "";
      this.user.first_name = "";
      this.user.last_name = "";
      this.user.email = "";
      this.user.role = "member";
      this.user.departments = [];
      if (this.orgs) {
        const org = this.orgs.find(({ key }) => key === "default");
        this.apps.forEach(app => {
          this.appUsers = {
            ...this.appUsers,
            [app.id]: {
              ...this.appUsers[app.id],
              role: "",
              org_id: this.currApp?.id === app.id && org?.id ? org.id : null,
            },
          };
        });
      }
    },
    onEditOrg(org) {
      this.showAddEditOrgDialog = true;
      this.org.id = org.id;
      this.org.name = org.name;
      this.org.key = org.key;
    },
    onEditUser(user) {
      this.showAddEditUserDialog = true;
      this.user.id = user.id;
      this.user.email = user.email;
      this.user.first_name = user.first_name;
      this.user.last_name = user.last_name;
      this.user.role = user.role;
      this.user.departments = user.departments;
      if (user.apps.length)
        user.apps.forEach(
          item => (this.appUsers[item.id] = { role: item.role, org_id: item.org_id }),
        );
      else
        Object.keys(this.appUsers).forEach(k => {
          this.appUsers[k] = { role: "", org_id: null };
        });
    },
    displayAllowedAppsText(user) {
      if ([ADMIN, SUPER_ADMIN].includes(user.role)) return "All";
      if (user.apps && user.apps.length)
        return user.apps.map(item => `${item.name} - ${item.role}`).join(", ");

      return "None";
    },
    async onDeleteOrg() {
      const id = this.orgToDelete;
      const params = {
        orgId: id,
        appId: this.currApp.id,
        newOrgId: this.reassignedOrgId,
      };
      const oldOrg = this.orgs.find(item => item.id === id);
      const newOrg = this.orgs.find(item => item.id === this.reassignedOrgId);
      await this.orgsStore.deleteOrg(params);
      this.toastsStore.create({
        type: "success",
        body: `${oldOrg.name} has been deleted and all items have been moved to ${newOrg.name}`,
      });
      this.closeDeleteOrgDialog();
    },
    closeDeleteOrgDialog() {
      this.orgToDelete = null;
      this.reassignedOrgId = null;
      this.showDeleteOrgDialog = false;
    },
    showConfirmDeleteOrgDialog(id) {
      this.orgToDelete = id;
      this.showDeleteOrgDialog = true;
    },
    async onSubmitOrg() {
      this.showAddEditOrgDialog = false;
      const { name } = this.org;
      if (name.length) {
        if (this.org.id) {
          await this.orgsStore.updateOrg({ appId: this.currApp.id, org: this.org });
          this.toastsStore.create({
            type: "success",
            body: `The ${name} organization has been updated`,
          });
          this.resetOrgForm();
        } else {
          await this.orgsStore.createOrg({ appId: this.currApp.id, name });
          this.toastsStore.create({
            type: "success",
            body: `The ${name} organization has been created`,
          });
          this.resetOrgForm();
        }
      }
    },
    onDeleteUser() {
      const id = this.userToDelete;
      const params = { userId: id, isAdminingCompany: this.isAdminingCompany };
      if (this.currApp) {
        params.appId = this.currApp.id;
      }
      this.usersStore.deleteUser(params);
      this.closeDeleteDialog();
    },
    onSubmitUser() {
      if (!this.isCorrect) return;
      const appUsers = [];
      this.showAddEditUserDialog = false;

      if (this.user.role === "member") {
        Object.keys(this.appUsers).forEach(k => {
          if (this.appUsers[k]?.role) {
            appUsers.push({
              app_id: k,
              role: this.appUsers[k].role,
              ...(this.appUsers[k]?.org_id && { org_id: this.appUsers[k].org_id }),
            });
          }
        });
      }

      this.user.apps = appUsers;
      const { first_name, last_name, email } = this.user;
      if (first_name.length && last_name.length && email.length) {
        if (this.user.id.length) {
          this.usersStore
            .updateUser({
              user: this.user,
              companyId: this.currentCompany?.id,
              updatePermissions: true,
            })
            .then(() => {
              this.resetUserForm();
            });
        } else {
          this.usersStore
            .inviteUser({
              user: this.user,
              companyId: this.currentCompany?.id,
            })
            .then(() => {
              this.resetUserForm();
            });
        }
      }
    },
    async onDeleteTestUser(user) {
      await this.endUserStore.remove({
        appId: this.currApp.id,
        endUserId: user.id,
      });
      this.getTestUsers();
    },
    async onResetGoals(user) {
      try {
        await ConduitApiUsers.resetGoal(this.currApp.id, user.user_uid);
        this.toastsStore.create({
          type: "success",
          body: `Goal reset successfully for user ${user.user_uid}`,
        });
      } catch (error) {
        useLegacyLastErrorStore().sendError(error);
      }
    },
    getTestUsers() {
      if (this.currApp) {
        this.endUserStore.search({
          query: "label=rf_test_user&value=true",
          appId: this.currApp.id,
        });
      }
    },

    async createTestUser() {
      const userIds = this.testUsers.map(user => user.user_uid);
      if (userIds.indexOf(this.newTestUserId) >= 0) {
        this.toastsStore.create({ type: "error", body: "Duplicate test user!" });
      } else {
        await this.endUserStore.create({
          appId: this.currApp.id,
          traits: {
            user_uid: this.newTestUserId,
            label: "rf_test_user",
            value: "true",
            metadata: {
              user_uid: this.newTestUserId,
              label: "description",
              value: this.newTestUserDesc,
            },
          },
        });
        this.getTestUsers();
        this.newTestUserId = null;
        this.newTestUserDesc = null;
      }
    },
    showConfirmDeleteDialog(id) {
      this.userToDelete = id;
      this.showDeleteUserDialog = true;
    },
    closeDeleteDialog() {
      this.userToDelete = null;
      this.showDeleteUserDialog = false;
    },
    async getActionsForEndUser() {
      if (this.userStore.isAdmin) {
        // Show selection dialog for admins
        this.userDataSelectionDialog = true;
      } else {
        // Non-admins get ping data directly
        await this.showPingData();
      }
    },
    async showPingData() {
      this.userDataSelectionDialog = false;
      await this.endUserStore.getActions({
        appId: this.currApp.id,
        userId: this.getActionsUserId,
        params: this.deviceType ? { device_type: this.deviceType } : undefined,
      });
      this.selectedDataType = "ping";
      this.actionsUserDataDialog = true;
    },
    async showDebugPingData() {
      this.userDataSelectionDialog = false;
      this.debugData = null;
      this.selectedDataType = "debug";
      this.actionsUserDataDialog = true;
      this.debugData = await ApiDebug.ping({
        appSlug: this.currApp.id,
        visitorId: this.getActionsUserId,
      });
    },
    async showTraitsData() {
      this.userDataSelectionDialog = false;
      this.traitsSearch = ""; // Clear search when opening
      await this.endUserStore.getUserTraits({
        appId: this.currApp.id,
        userId: this.getActionsUserId,
      });
      this.selectedDataType = "traits";
      this.actionsUserDataDialog = true;
    },
    segmentFilters({ name, matched, ...rest }) {
      return rest;
    },
    formatTimestamp(timestamp) {
      if (!timestamp) return "N/A";
      const date = new Date(timestamp);
      // Format with full date, time, and timezone
      return new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        second: "2-digit",
        timeZoneName: "short",
      }).format(date);
    },
    setDefaultDeviceType() {
      this.deviceType = this.devices[0].value;
    },
  },
};
</script>
<style>
.livepreview-domains .v-input--radio-group {
  margin-top: 0;
}
.add-testusers-id {
  display: flex;
  flex-direction: row;
  gap: 20px;
  margin-right: 20px;
  margin-top: -2px;
}
.add-testusers-id > div {
  flex: 1;
}
</style>
